<script lang="ts">
    import { onDestroy } from 'svelte'

    import { gettext as _ } from 'hydria/services/gettext'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import { sanchor, sgoto, spath } from 'hydria/svelte_helpers'

    import { d3, nv, nvReady } from 'wdom/utils/nvd'

    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    const STAGES = [
        { sol: 'addPeriod', label: 'Add Period', graph: true, table: true },
        { sol: 'transferPeriod', label: 'Transfer Period', graph: true, table: true },
        { sol: 'renewPeriod', label: 'Renew Period', graph: true, table: true },
        { sol: 'autoRenewPeriod', label: 'Auto Renew Period', graph: true, table: true },
        { sol: 'redemptionPeriod', label: 'Redemption Period', graph: true, table: true },
        { sol: 'pendingDelete', label: 'Pending Delete', graph: true, table: true },
        { sol: 'pendingRestore', label: 'Pending Restore', graph: true, table: true },
        { sol: 'registered', label: 'Registered', graph: false, table: true },
        { sol: 'unknown', label: 'Unknown', graph: true, table: true },
    ]

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    let chart: any
    let chartSvg: any

    interface TableEntry {
        label: string
        sol: string
        count: number
    }
    let gdata: TableEntry[] = []

    let solTable: TableEntry[] = []
    let total: number = $state(0)

    let showZeros = $state(false)

    let loading = $state(true)
    let updating = $state(false)
    let error = $state(false)
    let eventTimer: ReturnType<typeof setTimeout> | undefined = undefined

    let innerWidth: number = $state(0)
    function handleResize(w: number) {
        if (chart && chart.update) chart.update()
    }

    $effect(() => {
        handleResize(innerWidth)
    })

    onDestroy(() => {
        if (eventTimer) {
            try {
                clearTimeout(eventTimer)
            } catch {}
            eventTimer = undefined
        }
        if (chartSvg) {
            if (chart.tooltip && chart.tooltip.id) d3.select(`#${chart.tooltip.id()}`).remove()
            chartSvg.remove()
        }
    })

    if ($nvReady) load()

    async function load() {
        let r: SimpleFetchResult
        if (!loading) updating = true
        error = false

        try {
            r = await simpleFetch('GET', '/json/domain/1/statistics/stage-of-life/counts')
        } catch (e) {
            console.error('Failed to load SOL stats:', e)
            error = true
            loading = false
            updating = false
            return
        }

        gdata = []
        total = 0

        const allStages = new Set(STAGES.map((s) => s.sol))
        for (const s of STAGES) {
            allStages.add(s.sol)
            if (r.data.counts[s.sol] == null) r.data.counts[s.sol] = 0

            if (s.table)
                solTable.push({
                    label: s.label,
                    sol: s.sol,
                    count: r.data.counts[s.sol],
                })

            if (s.graph)
                gdata.push({
                    label: s.label,
                    sol: s.sol,
                    count: r.data.counts[s.sol],
                })

            total += r.data.counts[s.sol]
        }
        for (const [sol, cnt] of Object.entries(r.data.counts) as [string, number][]) {
            if (!allStages.has(sol)) {
                console.warn('Unaccounted SOL: ', sol)
                solTable.push({
                    label: `? ${sol}`,
                    sol: sol,
                    count: cnt,
                })
                gdata.push({
                    label: `? ${sol}`,
                    sol: sol,
                    count: cnt,
                })
            }
        }

        nv.addGraph(() => {
            chart = nv.models
                .pieChart()
                .x((d) => d.label)
                .y((d) => d.count)
                // .width(250)
                // .height(300)
                .id(
                    Math.random()
                        .toString(36)
                        .slice(2, 2 + 15),
                )
            chart
                .margin({
                    top: 100,
                    right: 10,
                    bottom: 15,
                    left: 10,
                })
                .donut(false)
                .donutRatio(0.35)
                .labelThreshold(0.002)
                .labelType('value')
                .labelsOutside(true)
                .showLabels(true)
                .showLegend(true)

            chart.pie.dispatch.on('elementClick', (e) => {
                sgoto(`/domains?stage_of_life=${e.data.sol}`)
            })

            chartSvg = d3.select('#sol-stat-graph')
            chartSvg.datum(gdata).call(chart)
        })

        loading = false
        updating = false
    }
</script>

<svelte:window bind:innerWidth />

<div class="card">
    <div class="card-title text-primary">
        <span>{$_('Stage of life')}</span>
        {#if updating}
            <span><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div></span>
        {/if}
        <span>
            <button type="button" class="btn btn-light btn-sm" class:active={showZeros} onclick={() => (showZeros = !showZeros)} style="font-feature-settings: 'zero' 1"><span>0</span></button>
        </span>
    </div>

    {#if loading}
        <div class="card-body d-flex gap-3">
            <div style="flex: 1 0 0">
                <LoadingPattern pattern={['x']} />
            </div>
            <div>{$_('Loading…')}</div>
            <div style="flex: 1 0 0">
                <LoadingPattern pattern={['x']} />
            </div>
        </div>
    {:else if error}
        <div class="card-body">
            <div class="text-danger">Les totaux de domaines n’ont pas pu être chargés.</div>
        </div>
    {:else}
        <div class="card-body pb-0">
            <svg id="sol-stat-graph" style="height: 275px; width: 100%" class="nvd3-svg" />
        </div>

        <div class="card-body">
            <table class="table mb-0">
                <tbody>
                    {#each solTable as i}
                        {#if showZeros || i.count}
                            <tr>
                                <td><a use:sanchor href={spath(`/domains?stage_of_life=${i.sol}`)}>{i.label}</a></td>
                                <td style="text-align: right; font-feature-settings: 'ss01' 1, 'tnum' 1;"><a use:sanchor href={spath(`/domains?stage_of_life=${i.sol}`)}>{i.count}</a></td>
                            </tr>
                        {/if}
                    {/each}
                    <tr>
                        <td style="font-weight: 500">Somme</td>
                        <td style="font-weight: 500; text-align: right; font-feature-settings: 'ss01' 1, 'tnum' 1;">{total}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    {/if}
</div>
